import React from 'react';
import styled from 'styled-components';

import {
  createIdempotencyKey,
  getVerificationCatalog,
  getVerificationChecks,
  getVerificationRisk,
  recomputeVerificationRisk,
  refreshVerificationCheck,
  requestVerificationChecks,
} from '../../api/rental';
import * as colors from '../../util/colors';
import {ActionButton, Actions, ErrorText, State} from './styles';
import {
  checkTitle,
  outcomeMeta,
  providerLabel,
  riskLabel,
  riskLevelMeta,
  statusMeta,
  triggerLabel,
} from './verification-view';

const REQUIREMENT_LABELS = {
  'file:passport_main': 'разворот паспорта',
  'file:driver_license_front': 'лицевая сторона ВУ',
  'file:driver_license_back': 'обратная сторона ВУ',
  'file:selfie_with_passport': 'селфи с открытым паспортом',
  'person.last_name': 'фамилия',
  'person.first_name': 'имя',
  'person.birth_date': 'дата рождения',
  phone: 'телефон',
  'passport.series_number': 'серия и номер паспорта',
  'passport.issue_date': 'дата выдачи паспорта',
  'driver_license.number': 'номер ВУ',
  'driver_license.issue_date': 'дата выдачи ВУ',
};

/**
 * Ответ провайдера показывается как есть: вложенные списки определений
 * растягивали записи на экраны, а оператору нужно быстро найти поле.
 */
function JsonView({value}) {
  return <JsonBlock>{JSON.stringify(value, null, 2)}</JsonBlock>;
}

function ResultDetails({check}) {
  if (!check.result && !check.request_params) { return null; }
  const records = check.result && (
    check.result.records || check.result.data || check.result.list
  );
  const count = Array.isArray(records) ? records.length : null;
  const resultLabel = check.check_type === 'check_bankrupt' && count !== null
    ? `Показать все записи и сообщения (${count})`
    : 'Показать полный ответ IDX';
  return (
    <Details>
      {check.result && (
        <details>
          <summary>{resultLabel}</summary>
          <SensitiveNote>
            Ответ содержит персональные данные. Просматривайте его только для решения по заявке.
          </SensitiveNote>
          <JsonView value={check.result} />
        </details>
      )}
      {check.request_params && (
        <details>
          <summary>Показать параметры запроса без ключей доступа</summary>
          <JsonView value={check.request_params} />
        </details>
      )}
    </Details>
  );
}

/**
 * Оценка риска клиента: считается сервером по последним результатам всех
 * проверок и только подсказывает оператору. Показываются факторы с весами,
 * незавершённые проверки и доводы в пользу клиента.
 */
function RiskSummary({risk, onRecompute, recomputing}) {
  if (!risk) { return null; }
  const meta = riskLevelMeta(risk.level);
  const details = risk.details || {};
  const factors = details.factors || [];
  const incomplete = details.incomplete || [];
  const positives = details.positives || [];
  const groups = details.groups || [];
  return (
    <RiskCard tone={meta.tone}>
      <RiskHeader>
        <div>
          <RiskTitle>Риск клиента</RiskTitle>
          <RiskAdvice>{risk.advice || meta.advice}</RiskAdvice>
        </div>
        <Pill tone={meta.tone}>{riskLabel(risk)}</Pill>
      </RiskHeader>
      {details.stop && (
        <RiskStop>Есть стоп-фактор: оценка критическая независимо от остальных проверок.</RiskStop>
      )}
      {factors.length > 0 && (
        <RiskSection>
          <RiskSectionTitle>Факторы риска</RiskSectionTitle>
          <RiskList>
            {factors.map((item, index) => (
              <li key={`${item.type}-${item.code}-${index}`}>
                <strong>{item.title}</strong> — {item.weight} %: {item.text}
                {item.confirm && <RiskFlag>требует сверки</RiskFlag>}
              </li>
            ))}
          </RiskList>
          {groups.length > 1 && !details.stop && (
            <RiskFormula>
              Группы: {groups.map(group => `${group.title} ${group.weight} %`).join('; ')}.
              Общая оценка — как независимые события: 1 − {groups.map(group => (1 - group.weight / 100).toFixed(2)).join(' × ')} = {risk.score} %.
            </RiskFormula>
          )}
        </RiskSection>
      )}
      {factors.length === 0 && risk.score !== null && (
        <RiskSection><RiskSectionTitle>Факторов риска не выявлено</RiskSectionTitle></RiskSection>
      )}
      {incomplete.length > 0 && (
        <RiskSection>
          <RiskSectionTitle>Не учтены ({incomplete.length})</RiskSectionTitle>
          <RiskList muted>
            {incomplete.map(item => <li key={item.type}>{item.title} — {item.reason}</li>)}
          </RiskList>
        </RiskSection>
      )}
      {positives.length > 0 && (
        <RiskSection>
          <RiskSectionTitle>Без замечаний</RiskSectionTitle>
          <RiskList muted>
            {positives.map((item, index) => <li key={`${item.type}-${index}`}>{item.text}</li>)}
          </RiskList>
        </RiskSection>
      )}
      <Meta>
        {risk.assessed_at
          ? `Оценка от ${new Date(risk.assessed_at).toLocaleString('ru-RU')}`
          : 'Оценка ещё не считалась'}
        {' · рекомендательная, решение принимает оператор'}
      </Meta>
      <Actions>
        <ActionButton type="button" disabled={recomputing} onClick={onRecompute}>
          {recomputing ? 'Пересчитываем…' : 'Пересчитать по текущим результатам'}
        </ActionButton>
      </Actions>
    </RiskCard>
  );
}

function VerificationPanel({authorization, userId, submissionId}) {
  const [catalog, setCatalog] = React.useState(null);
  const [checks, setChecks] = React.useState([]);
  const [risk, setRisk] = React.useState(null);
  const [selected, setSelected] = React.useState([]);
  const [running, setRunning] = React.useState(false);
  const [refreshing, setRefreshing] = React.useState(false);
  const [recomputing, setRecomputing] = React.useState(false);
  const [error, setError] = React.useState('');
  const requestAttempt = React.useRef(null);

  const load = React.useCallback(async () => {
    if (!authorization || !userId) { return; }
    setError('');
    try {
      const [catalogResponse, checksResponse, riskResponse] = await Promise.all([
        getVerificationCatalog(authorization),
        getVerificationChecks(authorization, userId, submissionId),
        getVerificationRisk(authorization, userId).catch(() => null),
      ]);
      setCatalog(catalogResponse);
      setChecks(checksResponse);
      setRisk(riskResponse);
    } catch (requestError) {
      setError(requestError.message);
    }
  }, [authorization, userId, submissionId]);

  React.useEffect(() => { load(); }, [load]);

  const providers = (catalog && catalog.providers) || [];
  const providerConfigured = name => providers.some(item => item.name === (name || 'idx') && item.configured);
  const configured = providerConfigured('idx');
  const anySelectable = providers.some(item => item.configured);
  const pending = checks.filter(check => check.status === 'pending' && check.operation_token);
  const selectedRunnable = selected.filter(type => {
    const check = catalog && catalog.checks.find(item => item.type === type);
    return check && providerConfigured(check.provider);
  });

  const recompute = async () => {
    setRecomputing(true);
    setError('');
    try {
      setRisk(await recomputeVerificationRisk(authorization, userId));
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setRecomputing(false);
    }
  };

  const groups = catalog && catalog.checks.reduce((result, check) => {
    const group = check.group || 'Другие проверки';
    if (!result[group]) { result[group] = []; }
    result[group].push(check);
    return result;
  }, {});

  const toggle = type => setSelected(current => (
    current.includes(type)
      ? current.filter(item => item !== type)
      : [...current, type]
  ));

  const request = async () => {
    if (!selected.length) { return; }
    setRunning(true);
    setError('');
    try {
      const signature = JSON.stringify([...selected].sort());
      if (!requestAttempt.current || requestAttempt.current.signature !== signature) {
        requestAttempt.current = {
          signature,
          key: createIdempotencyKey('verification'),
        };
      }
      await requestVerificationChecks(
        authorization,
        userId,
        {types: selected, submission_id: submissionId || null},
        requestAttempt.current.key,
      );
      requestAttempt.current = null;
      setSelected([]);
      await load();
    } catch (requestError) {
      // Ответ сервера завершает попытку (включая 4xx/5xx). Ключ сохраняется
      // только при обрыве сети, когда неизвестно, принял ли сервер команду.
      if (requestError.status) { requestAttempt.current = null; }
      setError(requestError.message);
    } finally {
      setRunning(false);
    }
  };

  const refreshPending = async () => {
    setRefreshing(true);
    setError('');
    try {
      const results = await Promise.all(pending.map(check => refreshVerificationCheck(
        authorization, check.id, createIdempotencyKey(`verification-refresh-${check.id}`),
      ).then(() => null).catch(requestError => requestError)));
      await load();
      const failure = results.find(result => result instanceof Error);
      if (failure) { setError(failure.message); }
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setRefreshing(false);
    }
  };

  if (!catalog) {
    return <Wrapper><State>Загрузка проверок…</State></Wrapper>;
  }

  return (
    <Wrapper>
      <Header>
        <strong>Внешние проверки</strong>
        <Hint>
          IDX даёт данные для анализа, но не одобряет и не отклоняет клиента.
          Итоговое решение всегда принимает оператор.
        </Hint>
      </Header>

      <RiskSummary risk={risk} onRecompute={recompute} recomputing={recomputing} />

      {!configured && (
        <State left>
          Провайдер IDX не подключён: доступны только локальные проверки. Задайте
          <code> IDX_ENABLED</code>, <code>IDX_ACCESS_KEY</code> и <code>IDX_SECRET_KEY</code>
          в окружении API.
        </State>
      )}

      <OrderNote>
        Сервер всегда сначала распознаёт выбранные документы, обновляет реквизиты,
        а затем запускает зависящие от них проверки. Порядок выбора не важен.
      </OrderNote>

      {Object.keys(groups || {}).map(group => (
        <CheckGroup key={group}>
          <GroupTitle>{group}</GroupTitle>
          <CheckList>
            {groups[group].map(check => (
              <CheckItem key={check.type}>
                <label>
                  <input
                    type="checkbox"
                    disabled={!providerConfigured(check.provider)}
                    checked={selected.includes(check.type)}
                    onChange={() => toggle(check.type)}
                  />
                  <span>
                    {check.title}
                    <CheckMeta>
                      {check.provider === 'local' ? 'локальная, бесплатно' : 'IDX, платный запрос'}
                      {check.recommended ? ' · в рекомендуемом наборе' : ''}
                    </CheckMeta>
                  </span>
                </label>
                {check.requires && check.requires.length > 0 && (
                  <Requirement>
                    Нужно: {check.requires.map(item => REQUIREMENT_LABELS[item] || item).join(', ')}
                  </Requirement>
                )}
                {check.note && <Hint>{check.note}</Hint>}
              </CheckItem>
            ))}
          </CheckList>
        </CheckGroup>
      ))}

      {error && <ErrorText role="alert">{error}</ErrorText>}

      <Actions>
        <ActionButton
          type="button"
          primary
          disabled={!anySelectable || running || !selectedRunnable.length}
          onClick={request}
        >
          {running ? 'Запрашиваем…' : `Запустить выбранные (${selectedRunnable.length})`}
        </ActionButton>
        <ActionButton
          type="button"
          disabled={!configured || refreshing || !pending.length}
          onClick={refreshPending}
        >
          {refreshing ? 'Получаем…' : `Получить готовые ответы (${pending.length})`}
        </ActionButton>
        <ActionButton type="button" disabled={running || refreshing} onClick={load}>
          Перезагрузить список
        </ActionButton>
        {selected.length > 0 && (
          <TextButton type="button" onClick={() => setSelected([])}>Снять выбор</TextButton>
        )}
      </Actions>

      {checks.length === 0 && (
        <EmptyResults>Проверки ещё не запускались.</EmptyResults>
      )}

      {checks.length > 0 && (
        <Results>
          {checks.map(check => {
            const technical = statusMeta(check.status);
            const business = outcomeMeta(check);
            const title = (catalog.checks.find(item => item.type === check.check_type) || {}).title;
            return (
              <ResultCard key={check.id}>
                <ResultHeader>
                  <div>
                    <ResultTitle>{title || checkTitle(check.check_type)}</ResultTitle>
                    <TechnicalCode>{check.check_type}</TechnicalCode>
                  </div>
                  <Pills>
                    <Pill tone={technical.tone}>Техника: {technical.label}</Pill>
                    <Pill tone={business.tone}>Итог: {business.label}</Pill>
                  </Pills>
                </ResultHeader>
                <Summary>{check.summary || check.result_message || 'Пояснение отсутствует'}</Summary>
                {check.result_message && check.summary && (
                  <TechnicalMessage>Сообщение IDX: {check.result_message}</TechnicalMessage>
                )}
                <Meta>
                  {new Date(check.created_at).toLocaleString('ru-RU')}
                  {` · ${providerLabel(check.provider)}`}
                  {check.trigger ? ` · ${triggerLabel(check.trigger)}` : ''}
                  {check.result_code !== null && check.result_code !== undefined && check.provider !== 'local'
                    ? ` · код IDX ${check.result_code}`
                    : ''}
                  {check.batch_id ? ` · пакет ${check.batch_id}` : ''}
                </Meta>
                <ResultDetails check={check} />
              </ResultCard>
            );
          })}
        </Results>
      )}
    </Wrapper>
  );
}

const Wrapper = styled.div`
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #e1e5ea;
`;

const Header = styled.div`margin-bottom: 12px;`;
const Hint = styled.div`
  margin-top: 4px;
  color: ${colors.TEXT_MINOR};
  font-size: 12px;
  line-height: 1.45;
`;
const OrderNote = styled.div`
  margin: 12px 0 18px;
  padding: 12px 14px;
  border-left: 4px solid ${colors.BLUE_MAIN};
  border-radius: 4px;
  color: ${colors.TEXT_MAIN};
  background: ${colors.BLUE_LIGHT};
  font-size: 13px;
  line-height: 1.5;
`;
const CheckGroup = styled.section`margin: 18px 0;`;
const GroupTitle = styled.h3`
  margin: 0 0 10px;
  color: ${colors.TEXT_TITLE};
  font-size: 14px;
`;
const CheckList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 10px;
`;
const CheckItem = styled.div`
  padding: 12px;
  border: 1px solid ${colors.LINE};
  border-radius: 6px;
  font-size: 13px;
  label { display: flex; align-items: flex-start; gap: 9px; margin: 0; cursor: pointer; }
  input { width: 18px; height: 18px; flex: 0 0 auto; }
`;
const Requirement = styled.div`
  margin: 7px 0 0 27px;
  color: ${colors.TEXT_MINOR};
  font-size: 12px;
  line-height: 1.4;
`;
const CheckMeta = styled.span`
  display: block;
  margin-top: 3px;
  color: ${colors.TEXT_MINOR};
  font-size: 11px;
`;
const RiskCard = styled.section`
  margin: 0 0 18px;
  padding: 16px;
  border: 1px solid ${props => ({
    danger: '#d83a20', attention: '#d83a20', information: '#2c6ecb', clear: '#2d7d32',
  }[props.tone] || colors.LINE)};
  border-left-width: 5px;
  border-radius: 8px;
  background: #fff;
`;
const RiskHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
`;
const RiskTitle = styled.strong`
  display: block;
  color: ${colors.TEXT_TITLE};
  font-size: 15px;
`;
const RiskAdvice = styled.div`
  margin-top: 3px;
  color: ${colors.TEXT_MINOR};
  font-size: 12px;
`;
const RiskStop = styled.div`
  margin-top: 12px;
  padding: 8px 10px;
  border-radius: 6px;
  color: #a82412;
  background: #fff0ed;
  font-size: 13px;
  font-weight: 700;
`;
const RiskSection = styled.div`margin-top: 12px;`;
const RiskSectionTitle = styled.div`
  margin-bottom: 4px;
  color: ${colors.TEXT_TITLE};
  font-size: 13px;
  font-weight: 700;
`;
const RiskList = styled.ul`
  margin: 0;
  padding-left: 18px;
  color: ${props => (props.muted ? colors.TEXT_MINOR : colors.TEXT_MAIN)};
  font-size: 13px;
  line-height: 1.5;
  li { margin: 2px 0; }
`;
const RiskFlag = styled.span`
  display: inline-block;
  margin-left: 6px;
  padding: 1px 6px;
  border-radius: 999px;
  color: #a82412;
  background: #fff0ed;
  font-size: 11px;
  font-weight: 700;
`;
const RiskFormula = styled.div`
  margin-top: 8px;
  color: ${colors.TEXT_MINOR};
  font-size: 12px;
  line-height: 1.5;
`;
const TextButton = styled.button`
  min-height: 44px;
  padding: 0 8px;
  border: 0;
  color: ${colors.BLUE_MAIN};
  background: transparent;
  cursor: pointer;
  font-weight: 600;
  &:focus { outline: 3px solid ${colors.BLUE_LIGHT}; }
`;
const EmptyResults = styled.div`
  margin-top: 18px;
  color: ${colors.TEXT_MINOR};
  font-size: 13px;
`;
const Results = styled.div`
  display: grid;
  gap: 12px;
  margin-top: 20px;
`;
const ResultCard = styled.article`
  padding: 16px;
  border: 1px solid ${colors.LINE};
  border-radius: 8px;
  background: #fff;
`;
const ResultHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
`;
const ResultTitle = styled.strong`
  display: block;
  color: ${colors.TEXT_TITLE};
  font-size: 14px;
`;
const TechnicalCode = styled.code`
  color: ${colors.TEXT_MINOR};
  font-size: 11px;
`;
const Pills = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;
const Pill = styled.span`
  display: inline-flex;
  min-height: 26px;
  align-items: center;
  padding: 4px 9px;
  border: 1px solid ${props => ({
    danger: '#d83a20',
    attention: '#d83a20',
    clear: '#2d7d32',
    pending: '#2c6ecb',
    complete: '#65707c',
    information: '#2c6ecb',
    neutral: '#8b949f',
  }[props.tone] || '#8b949f')};
  border-radius: 999px;
  color: ${props => ({
    danger: '#a82412',
    attention: '#a82412',
    clear: '#236327',
    pending: '#194f9c',
    complete: '#4c5661',
    information: '#194f9c',
    neutral: '#59636e',
  }[props.tone] || '#59636e')};
  background: ${props => ({
    danger: '#fff0ed',
    attention: '#fff0ed',
    clear: '#ecf9e7',
    pending: '#eef5ff',
    complete: '#f2f4f6',
    information: '#eef5ff',
    neutral: '#f2f4f6',
  }[props.tone] || '#f2f4f6')};
  font-size: 12px;
  font-weight: 700;
`;
const Summary = styled.div`
  margin-top: 12px;
  color: ${colors.TEXT_MAIN};
  font-size: 14px;
  line-height: 1.5;
`;
const TechnicalMessage = styled.div`
  margin-top: 6px;
  color: ${colors.ERROR};
  font-size: 12px;
`;
const Meta = styled.div`
  margin-top: 8px;
  color: ${colors.TEXT_MINOR};
  font-size: 11px;
  overflow-wrap: anywhere;
`;
const Details = styled.div`
  margin-top: 12px;
  details { margin-top: 8px; }
  summary {
    min-height: 32px;
    color: ${colors.BLUE_MAIN};
    cursor: pointer;
    font-size: 13px;
    font-weight: 700;
  }
  summary:focus { outline: 3px solid ${colors.BLUE_LIGHT}; }
`;
const SensitiveNote = styled.div`
  margin: 6px 0 12px;
  padding: 8px 10px;
  color: ${colors.TEXT_MINOR};
  background: ${colors.BG};
  font-size: 12px;
`;
const JsonBlock = styled.pre`
  margin: 8px 0 10px;
  padding: 12px;
  border-left: 2px solid ${colors.LINE};
  color: ${colors.TEXT_MAIN};
  background: ${colors.BG};
  font-family: 'SFMono-Regular', Menlo, Consolas, monospace;
  font-size: 12px;
  line-height: 1.5;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  max-height: 520px;
  overflow: auto;
`;

export default VerificationPanel;
